import { useEffect, useState } from "react";
import logo from "@/assets/uvashop-logo.webp";
import { Instagram, Facebook } from "lucide-react";
import TrustBadges from "@/components/TrustBadges";
import { api } from "@/lib/api";

const REGION_PREP = {
  "Veneto": "del Veneto", "Toscana": "della Toscana", "Piemonte": "del Piemonte",
  "Lombardia": "della Lombardia", "Sicilia": "della Sicilia", "Sardegna": "della Sardegna",
  "Puglia": "della Puglia", "Campania": "della Campania", "Lazio": "del Lazio",
  "Umbria": "dell'Umbria", "Marche": "delle Marche", "Abruzzo": "dell'Abruzzo",
  "Friuli-Venezia Giulia": "del Friuli", "Friuli Venezia Giulia": "del Friuli", "Friuli": "del Friuli",
  "Trentino-Alto Adige": "del Trentino", "Trentino Alto Adige": "del Trentino", "Trentino": "del Trentino",
  "Emilia-Romagna": "dell'Emilia-Romagna", "Emilia Romagna": "dell'Emilia-Romagna",
  "Liguria": "della Liguria", "Calabria": "della Calabria", "Basilicata": "della Basilicata",
  "Molise": "del Molise", "Valle d'Aosta": "della Valle d'Aosta",
};
const regionLabel = (r) => `Vini ${REGION_PREP[r] || r}`;

export default function Footer() {
  const [regions, setRegions] = useState([]);
  useEffect(() => { api.get("/regions").then((r) => setRegions(r.data)).catch(() => {}); }, []);

  return (
    <>
      <div data-testid="footer-divider" className="h-[75px] bg-bone" aria-hidden="true" />
      <footer data-testid="site-footer" className="relative z-[2] bg-ink text-bone">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20 grid md:grid-cols-4 gap-12">
        <div className="md:col-span-2">
          <img src={logo} alt="UvaShop" width="749" height="186" className="h-12 lg:h-14 w-auto" />
          <p className="mt-6 text-bone/75 max-w-md leading-relaxed">
            UvaShop è l'e-commerce ufficiale di Enoteca Uvaspina a Rovigo.
          </p>
          <p className="mt-3 text-bone/55 max-w-md text-sm leading-relaxed" data-testid="footer-legal-entity">
            LIQUIDA DI CITTANTE CARLO E C. S.N.C. · P.IVA 01581660295 · Via del Sacro Cuore 2, 45100 Rovigo (RO)
          </p>
          <div className="flex items-center gap-4 mt-7">
            <a href="https://www.instagram.com/uvaspinaenoteca" target="_blank" rel="noopener noreferrer" aria-label="Instagram Enoteca Uvaspina" data-testid="footer-instagram" className="w-11 h-11 border border-bone/20 hover:border-bone hover:bg-bone hover:text-ink flex items-center justify-center transition-colors">
              <Instagram size={19} strokeWidth={1.6} />
            </a>
            <a href="https://www.facebook.com/uvaspinaenoteca/?locale=it_IT" target="_blank" rel="noopener noreferrer" aria-label="Facebook Enoteca Uvaspina" data-testid="footer-facebook" className="w-11 h-11 border border-bone/20 hover:border-bone hover:bg-bone hover:text-ink flex items-center justify-center transition-colors">
              <Facebook size={19} strokeWidth={1.6} />
            </a>
          </div>
        </div>
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-bone/70 mb-5">Navigazione</p>
          <ul className="space-y-3 text-bone/80">
            <li><a href="/enoteca-rovigo" data-testid="footer-chi-siamo" className="link-underline">Chi siamo</a></li>
            <li><a href="/shop" className="link-underline">Catalogo</a></li>
            <li><a href="/shop?type=sparkling" className="link-underline">Bollicine</a></li>
            <li><a href="/shop?type=white" className="link-underline">Bianchi</a></li>
            <li><a href="/shop?type=red" className="link-underline">Rossi</a></li>
            <li><a href="/shop?type=distillati" className="link-underline">Distillati</a></li>
            <li><a href="/spedizioni" data-testid="footer-shipping" className="link-underline">Spedizioni e Resi</a></li>
            <li><a href="/aziende" data-testid="footer-aziende" className="link-underline">Aziende</a></li>
            <li><a href="/faq" data-testid="footer-faq" className="link-underline">Domande Frequenti</a></li>
          </ul>
        </div>
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-bone/70 mb-5">Contatti</p>
          <ul className="space-y-3 text-bone/80">
            <li>Via Sacro Cuore 2, 45100 Rovigo</li>
            <li><a href="tel:+393928160004" data-testid="footer-phone" className="link-underline hover:text-bone transition-colors">Tel. 392 816 0004</a></li>
            <li><a href="https://wa.me/393928160004" target="_blank" rel="noopener noreferrer" data-testid="footer-whatsapp" className="link-underline hover:text-bone transition-colors">WhatsApp</a></li>
            <li>uvaspinaenoteca@gmail.com</li>
          </ul>
        </div>
      </div>
      {regions.length > 0 && (
        <div className="border-t border-bone/10" data-testid="footer-regions">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-10">
            <p className="text-xs uppercase tracking-[0.2em] text-bone/70 mb-5">Vini per regione</p>
            <div className="flex flex-wrap gap-x-6 gap-y-3 text-bone/80 text-sm">
              {regions.map((r) => (
                <a key={r} href={`/shop?region=${encodeURIComponent(r)}`} data-testid={`footer-region-${r}`} className="link-underline hover:text-bone transition-colors">
                  {regionLabel(r)}
                </a>
              ))}
            </div>
          </div>
        </div>
      )}
      <div className="border-t border-bone/10 py-8">
        <TrustBadges variant="dark" />
      </div>
      <div className="border-t border-bone/10">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-6 flex flex-col md:flex-row md:items-center justify-between text-xs text-bone/70 gap-4">
          <span>© 2026 UvaShop · P.IVA: IT01581660295 · Bevi responsabilmente.</span>
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
            <a href="/privacy" data-testid="footer-privacy" className="link-underline hover:text-bone/80 transition-colors">Privacy Policy</a>
            <a href="/termini" data-testid="footer-terms" className="link-underline hover:text-bone/80 transition-colors">Termini e Condizioni</a>
            <button type="button" data-testid="footer-cookie-prefs" onClick={() => window.dispatchEvent(new Event("cookie:open"))} className="link-underline hover:text-bone/80 transition-colors">Preferenze cookie</button>
            <span>Vietata la vendita ai minori di 18 anni.</span>
          </div>
        </div>
      </div>
    </footer>
    </>
  );
}
